import { useState } from 'react';
import { ArrowDown, ArrowDownRight, ArrowUpRight, Minus, TriangleAlert } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  biggestDrop,
  formatRate,
  pointsDelta,
  type ConversionFunnel,
  type ConversionGroup,
} from '@/lib/admin/conversion-funnels';

const GROUPS: { id: ConversionGroup; title: string }[] = [
  { id: 'entry', title: 'Inscription' },
  { id: 'buy', title: 'Achat' },
  { id: 'invite', title: 'Invitations et défis' },
];

const INT = new Intl.NumberFormat('fr-FR');

function DeltaPill({ rate, prevRate }: { rate: number | null; prevRate: number | null }) {
  const d = pointsDelta(rate, prevRate);
  if (!d) return <span className="text-ink-400 text-xs">pas de comparaison</span>;
  const Icon = d.dir === 'up' ? ArrowUpRight : d.dir === 'down' ? ArrowDownRight : Minus;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
        d.dir === 'up' && 'bg-emerald-50 text-emerald-700',
        d.dir === 'down' && 'bg-red-50 text-red-700',
        d.dir === 'flat' && 'bg-ink-50 text-ink-500',
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {d.label}
    </span>
  );
}

function FunnelTile({
  funnel,
  selected,
  onSelect,
}: {
  funnel: ConversionFunnel;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex min-h-[2.75rem] flex-col gap-1 rounded-2xl border p-3 text-left transition-colors active:scale-[0.98]',
        selected
          ? 'border-primary-400 bg-primary-50 ring-primary-100 ring-2'
          : 'border-ink-200 hover:border-primary-300 bg-white',
      )}
    >
      <span className="text-ink-700 text-sm font-semibold leading-tight">{funnel.label}</span>
      <span className="flex items-baseline gap-2">
        <span className="font-display text-ink-900 text-2xl font-extrabold tabular-nums">
          {formatRate(funnel.rate)}
        </span>
        <DeltaPill rate={funnel.rate} prevRate={funnel.prevRate} />
      </span>
      <span className="text-ink-500 text-xs tabular-nums">
        {INT.format(funnel.converted)} sur {INT.format(funnel.started)} visiteur
        {funnel.started > 1 ? 's' : ''}
      </span>
    </button>
  );
}

/** Le tunnel en barres : chaque étape garde les visiteurs de la précédente. */
export function FunnelChart({ funnel }: { funnel: ConversionFunnel }) {
  const drop = biggestDrop(funnel);
  return (
    <ol className="flex flex-col" aria-label={`Étapes · ${funnel.label}`}>
      {funnel.steps.map((s, i) => {
        const width = s.count > 0 ? Math.max(s.ofFirst * 100, 1.5) : 0;
        return (
          <li key={s.label} className="flex flex-col">
            {i > 0 && (
              <p
                className={cn(
                  'flex items-center gap-1.5 py-1.5 pl-1 text-xs',
                  drop === i ? 'font-semibold text-amber-800' : 'text-ink-500',
                )}
              >
                {drop === i ? (
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
                ) : (
                  <ArrowDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
                <span className="tabular-nums">
                  {formatRate(s.ofPrevStep)} passent
                  {s.lost > 0 && ` · ${INT.format(s.lost)} perdu${s.lost > 1 ? 's' : ''}`}
                  {drop === i && ' · plus grosse perte'}
                </span>
              </p>
            )}
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-ink-800 min-w-0 truncate text-sm font-semibold">{s.label}</span>
              <span className="text-ink-900 shrink-0 text-sm font-bold tabular-nums">
                {INT.format(s.count)}
                <span className="text-ink-500 ml-1.5 font-normal">
                  {i === 0 ? '' : formatRate(s.ofFirst)}
                </span>
              </span>
            </div>
            <div className="bg-ink-100 mt-1 h-3 w-full overflow-hidden rounded-full" aria-hidden>
              <div className="bg-primary-500 h-full rounded-full" style={{ width: `${width}%` }} />
            </div>
            <span className="text-ink-400 mt-0.5 text-xs tabular-nums">
              Période d’avant : {INT.format(s.prev)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Tunnels de conversion (migration 0718) : un tableau de bord de taux (une
 * tuile par tunnel, cliquable) et le détail du tunnel choisi en barres.
 */
export function ConversionPanel({
  funnels,
  rangeLabel,
}: {
  funnels: ConversionFunnel[];
  rangeLabel: string;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const current = funnels.find((f) => f.key === selected) ?? funnels[0];
  if (!current) return null;
  return (
    <section aria-labelledby="conversion-title" className="flex flex-col gap-4">
      <div>
        <h2 id="conversion-title" className="font-display text-ink-900 text-lg font-bold">
          Taux de conversion · {rangeLabel}
        </h2>
        <p className="text-ink-500 text-sm">
          Les mêmes visiteurs suivis étape par étape, dans l’ordre. Écart en points avec la période
          d’avant de même durée. Touche un tunnel pour voir ses étapes.
        </p>
      </div>
      {GROUPS.map((g) => {
        const list = funnels.filter((f) => f.group === g.id);
        if (list.length === 0) return null;
        return (
          <div key={g.id} className="flex flex-col gap-2">
            <h3 className="text-ink-500 text-xs font-semibold uppercase tracking-wider">
              {g.title}
            </h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {list.map((f) => (
                <FunnelTile
                  key={f.key}
                  funnel={f}
                  selected={f.key === current.key}
                  onSelect={() => setSelected(f.key)}
                />
              ))}
            </div>
          </div>
        );
      })}
      <div className="border-ink-200 rounded-2xl border bg-white p-4">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-display text-ink-900 text-base font-bold">{current.label}</h3>
          <span className="text-ink-600 text-sm">
            Conversion{' '}
            <strong className="text-ink-900 tabular-nums">{formatRate(current.rate)}</strong>{' '}
            <DeltaPill rate={current.rate} prevRate={current.prevRate} />
          </span>
        </div>
        <FunnelChart funnel={current} />
        {current.key.startsWith('site') && (
          <p className="text-ink-500 mt-3 text-xs">
            Le passage du site à l’app est suivi depuis le 30 octobre 2026 : les étapes après la
            visite du site se remplissent à partir de cette date.
          </p>
        )}
        {current.group === 'invite' && (
          <p className="text-ink-500 mt-3 text-xs">
            Défis SKILLCARD ouverts dans l’app et défis partagés suivis depuis le 30 octobre 2026.
            Les invités d’un défi privé (sans compte) sont comptés comme visiteurs.
          </p>
        )}
        {current.newOnly && (
          <p className="text-ink-500 mt-2 text-xs">
            Nouveaux visiteurs seulement : un compte qui existait avant la 1re étape n’est pas
            compté.
          </p>
        )}
      </div>
    </section>
  );
}
